import { useEffect, useState, useCallback } from "react";

import { PatchPatientBodyBodies } from "api-client";

// 既存のuseSessionStorageフック（userキー用）
export const useSessionStorage = (
  key: "user"
): [PatchPatientBodyBodies | null, (value: PatchPatientBodyBodies) => void] => {
  const temp = sessionStorage.getItem(key);
  const [userData, setUserData] = useState<PatchPatientBodyBodies | null>(
    JSON.parse(temp as string)
  );

  useEffect(() => {
    const handleStorageEvent = (event: StorageEvent) => {
      if (event.key === key) {
        setUserData(event.newValue as PatchPatientBodyBodies | null);
      }
    };

    window.addEventListener("storage", handleStorageEvent);

    return () => {
      window.removeEventListener("storage", handleStorageEvent);
    };
  }, [key]);

  const updateUserData = useCallback(
    (newValue: PatchPatientBodyBodies) => {
      const temp = JSON.stringify(newValue);
      sessionStorage.setItem(key, temp);
      setUserData(newValue);
    },
    [key]
  );

  return [userData, updateUserData];
};

// 汎用的なセッションストレージフック
export const useGenericSessionStorage = <T>(
  key: string,
  initialValue?: T
): [T | null, (value: T | null) => void] => {
  const [storedValue, setStoredValue] = useState<T | null>(() => {
    try {
      const item = sessionStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue || null;
    } catch (error) {
      console.error(`Error reading sessionStorage key "${key}":`, error);
      return initialValue || null;
    }
  });

  useEffect(() => {
    const handleStorageEvent = (event: StorageEvent) => {
      if (event.key === key) {
        try {
          const newValue = event.newValue ? JSON.parse(event.newValue) : null;
          setStoredValue(newValue);
        } catch (error) {
          console.error(`Error parsing sessionStorage value for key "${key}":`, error);
          setStoredValue(null);
        }
      }
    };

    window.addEventListener("storage", handleStorageEvent);

    return () => {
      window.removeEventListener("storage", handleStorageEvent);
    };
  }, [key]);

  const setValue = useCallback(
    (value: T | null) => {
      try {
        setStoredValue(value);
        if (value === null) {
          sessionStorage.removeItem(key);
        } else {
          sessionStorage.setItem(key, JSON.stringify(value));
        }
      } catch (error) {
        console.error(`Error setting sessionStorage key "${key}":`, error);
      }
    },
    [key]
  );

  return [storedValue, setValue];
};
